import { useEffect, useRef, useState, type FormEvent } from "react";
import { PageHeader, Panel } from "../components/Surface";
import { Button } from "../components/ui/Button";
import { inputClass } from "../components/FormKit";
import { uploadToSignedUrl } from "../lib/verification";
import {
  listIncidents, reportIncident, incidentMediaUrl, finalizeIncidentMedia, reviewIncident,
  INCIDENT_KINDS, INCIDENT_LABELS, type Incident, type IncidentKind, type ReviewStatus,
} from "../lib/incidents";

const SEV_LABEL: Record<number, string> = { 1: "Minor", 2: "Moderate", 3: "Severe" };
const REVIEW_STYLE: Record<ReviewStatus, string> = { open: "text-[#c9a227]", reviewed: "text-verified", dismissed: "text-muted" };

export function Incidents() {
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ kind: "near_miss" as IncidentKind, severity: 2, note: "" });
  const fileRef = useRef<HTMLInputElement>(null);

  async function refresh() { setIncidents((await listIncidents()).incidents); }
  useEffect(() => { refresh().finally(() => setLoading(false)); }, []);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const { id } = await reportIncident({ kind: form.kind, severity: form.severity, note: form.note || undefined });
      const file = fileRef.current?.files?.[0];
      if (file) {
        const { path, upload_url } = await incidentMediaUrl(id);
        await uploadToSignedUrl(upload_url, file);
        await finalizeIncidentMedia(id, path);
      }
      setForm({ kind: "near_miss", severity: 2, note: "" });
      if (fileRef.current) fileRef.current.value = "";
      await refresh();
    } finally { setBusy(false); }
  }

  async function mark(id: string, status: ReviewStatus) {
    setBusy(true);
    try { await reviewIncident(id, status); await refresh(); } finally { setBusy(false); }
  }

  return (
    <div className="mx-auto grid w-full max-w-3xl gap-6">
      <PageHeader
        title="Safety incidents"
        subtitle="Capture collisions, near-misses, and roadside events with a photo or dashcam clip — from the phone you already have. Media is kept private for 90 days."
      />

      <Panel title="Report an incident">
        <form className="grid gap-3 sm:grid-cols-2" onSubmit={submit}>
          <select className={inputClass} value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value as IncidentKind })} aria-label="Incident type">
            {INCIDENT_KINDS.map((k) => <option key={k} value={k}>{INCIDENT_LABELS[k]}</option>)}
          </select>
          <select className={inputClass} value={form.severity} onChange={(e) => setForm({ ...form, severity: Number(e.target.value) })} aria-label="Severity">
            {[1, 2, 3].map((s) => <option key={s} value={s}>{SEV_LABEL[s]}</option>)}
          </select>
          <input className={`${inputClass} sm:col-span-2`} placeholder="What happened? (optional)" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
          <input ref={fileRef} type="file" accept="image/*,video/mp4,video/quicktime,video/webm" className="text-sm sm:col-span-2" aria-label="Incident media" />
          <Button type="submit" disabled={busy} className="sm:col-span-2" data-testid="report-incident">Report incident</Button>
        </form>
      </Panel>

      {loading ? (
        <p className="font-mono text-sm text-muted">Loading…</p>
      ) : incidents.length === 0 ? (
        <p className="font-mono text-sm text-muted">No incidents logged. Report one above if something happens on the road.</p>
      ) : (
        <Panel title={`Incidents (${incidents.length})`}>
          <ul className="grid gap-4" data-testid="incident-list">
            {incidents.map((i) => (
              <li key={i.id} className="grid gap-2 border-b border-line pb-4 last:border-0 last:pb-0">
                <div className="flex items-center justify-between gap-3">
                  <span className="font-bold">{INCIDENT_LABELS[i.kind]} · <span className="text-muted">{SEV_LABEL[i.severity]}</span></span>
                  <span className={`font-mono text-[11px] font-bold uppercase ${REVIEW_STYLE[i.review_status]}`}>{i.review_status}</span>
                </div>
                <span className="font-mono text-[11px] text-muted">{new Date(i.occurred_at).toLocaleString()}</span>
                {i.note && <span className="text-sm">{i.note}</span>}
                {i.media_url && i.media_type?.startsWith("image/") && (
                  <img src={i.media_url} alt="incident" className="max-h-56 w-auto rounded-lg border border-line" />
                )}
                {i.media_url && i.media_type?.startsWith("video/") && (
                  <video src={i.media_url} controls className="max-h-56 w-auto rounded-lg border border-line" data-testid="incident-video" />
                )}
                {i.review_status === "open" && (
                  <span className="flex gap-2">
                    <button type="button" className="font-mono text-[11px] text-verified hover:underline" disabled={busy} onClick={() => mark(i.id, "reviewed")}>Mark reviewed</button>
                    <button type="button" className="font-mono text-[11px] text-muted hover:underline" disabled={busy} onClick={() => mark(i.id, "dismissed")}>Dismiss</button>
                  </span>
                )}
              </li>
            ))}
          </ul>
        </Panel>
      )}
    </div>
  );
}
